/**
 * Circularity figures shared by the Circularity pages and the HTML report.
 * Inputs are the rows of `combined_data.combined_circularities` (see CircularityFunctions.js),
 * so every value here is derived from the same per-material calculation the app shows.
 */
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { toFiniteNumber } from "./formatters";

// Circular shares of each side of the material flow (everything except virgin input / waste output).
export const INPUT_CIRCULAR_KEYS = ["im_recycled_content", "im_reuse", "im_remanufactured", "im_bio_materials"];
export const OUTPUT_CIRCULAR_KEYS = ["om_recycling", "om_reuse", "om_bio_composted", "om_bio_energy_recovery"];

const clampPercent = (value) => Math.min(100, Math.max(0, toFiniteNumber(value)));

const sumKeys = (row, keys) => keys.reduce((sum, key) => sum + clampPercent(row[key]), 0);

/** Share of the input that is virgin feedstock, 0-100. */
export const virginInputPercent = (row) => Math.max(0, 100 - sumKeys(row, INPUT_CIRCULAR_KEYS));

/** Share of the output sent to landfill / waste, 0-100. */
export const wasteOutputPercent = (row) => Math.max(0, 100 - sumKeys(row, OUTPUT_CIRCULAR_KEYS));

/** Mass-weighted average of a per-row percentage. */
export const massWeightedPercent = (rows, getPercent) => {
  const totalMass = rows.reduce((sum, r) => sum + toFiniteNumber(r.mass), 0);
  if (totalMass <= 0) return 0;
  return rows.reduce((sum, r) => sum + getPercent(r) * toFiniteNumber(r.mass), 0) / totalMass;
};

/** Product / project level flow summary from the per-material rows. */
export const summarizeCircularityFlows = (rows = []) => ({
  virginInputPercent: massWeightedPercent(rows, virginInputPercent),
  recycledInputPercent: massWeightedPercent(rows, (r) => clampPercent(r.im_recycled_content)),
  reusedInputPercent: massWeightedPercent(rows, (r) => clampPercent(r.im_reuse)),
  recoveryPercent: massWeightedPercent(rows, (r) => 100 - wasteOutputPercent(r)),
  wasteOutputPercent: massWeightedPercent(rows, wasteOutputPercent),
});

// Categorical series colours for per-material charts, taken from the circularity flow tokens.
const MATERIAL_SERIES_FLOWS = ["recycled", "reuse", "energyRecovery", "remanufactured", "biological", "virgin", "waste"];

export const getMaterialSeriesColor = (index) => GRAPH_PALETTE.circularityFlows[MATERIAL_SERIES_FLOWS[index % MATERIAL_SERIES_FLOWS.length]].light;

/**
 * Keeps the largest `maxItems - 1` entries and folds the rest into one "Others" entry,
 * matching the slice limit of the in-app donut charts.
 *
 * @param {Array<{ label, value }>} items
 * @returns {Array<{ label, value, isOthers? }>}
 */
export const groupTopItems = (items, maxItems, othersLabel) => {
  const sorted = [...items].filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  if (sorted.length <= maxItems) return sorted;
  const top = sorted.slice(0, maxItems - 1);
  const othersValue = sorted.slice(maxItems - 1).reduce((sum, i) => sum + i.value, 0);
  return [...top, { label: othersLabel, value: othersValue, isOthers: true }];
};
